import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import './blog.css';

// ⚙️ SET THESE ──────────────────────────────────────────────────────────────
// Where readers land when they click a card. Your blog app serves full posts
// at /post/:slug, so this is just the origin — the "/post/" is added below.
const BLOG_URL = 'https://blog.spinosoftbits.com';

// Live FastAPI blog API base (Railway). The carousel fetches `${BLOG_API_URL}/posts/`.
// Plain constant on purpose: this main site is Create React App, so `import.meta.env`
// would break the build. Override here (or swap to process.env.REACT_APP_BLOG_API_URL).
const BLOG_API_URL =
    process.env.REACT_APP_BLOG_API_URL ||
    'https://spinosoftbits-blog-production.up.railway.app/api';

// ─────────────────────────────────────────────────────────────────────────────

// Vibrant, white-text-friendly gradients. Known categories keep a semantic
// color; anything else gets a stable pick from this palette so cards don't
// all collapse to the same default purple.
// Each post gets a distinct, curated multi-stop gradient (stable per post),
// with text auto-set to light or dark so it always stays readable.
const ART_PALETTES = [
    // Warm & earthy
    { stops: ['#5A3A2E', '#A45A3B', '#E2A76F'], text: 'light' }, // terracotta → caramel
    { stops: ['#3E2723', '#6F4E37', '#C8A165'], text: 'light' }, // cacao & caramel
    { stops: ['#C99700', '#7E9B6B', '#2C5F2D'], text: 'light' }, // mustard · sage · forest
    // Bold & vibrant
    { stops: ['#1F2933', '#3E4C59', '#F2C200'], text: 'light' }, // dark gray & bright yellow
    { stops: ['#16A6A6', '#4453B8', '#7A2BD6'], text: 'light' }, // turquoise → violet
    { stops: ['#EE0979', '#FF6A00', '#FFC837'], text: 'light' }, // magma
    // Jewel
    { stops: ['#0F2027', '#203A43', '#2C7A6E'], text: 'light' }, // deep teal
    { stops: ['#6A0572', '#AB2C6E', '#E94E77'], text: 'light' }, // plum → rose
    { stops: ['#8E0E00', '#D2042D', '#FF6B6B'], text: 'light' }, // cherry red
    // Soft & romantic (dark text)
    { stops: ['#FFE5D4', '#FFC1A6', '#FFA8C5'], text: 'dark' }, // peach & baby pink
    { stops: ['#BFD7ED', '#D9C2E9', '#F6C5D5'], text: 'dark' }, // baby blue · lilac · pink
    { stops: ['#B8F2E6', '#AED9E0', '#EAF7FF'], text: 'dark' }, // mint → soft white
    { stops: ['#9EC8FF', '#C9B6FF', '#FFB7E0'], text: 'dark' }, // sky blue → bubblegum
];

// Stable hash so each post keeps the same palette across renders/rotation.
const hashKey = (v) => {
    const s = String(v ?? '');
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return h;
};

// Resolve a post -> { gradient, ink, badgeBg, badgeBorder } with readable text.
const paletteFor = (post) => {
    const p = ART_PALETTES[hashKey(post?.id ?? post?.category) % ART_PALETTES.length];
    const n = p.stops.length;
    const gradient = `linear-gradient(135deg, ${p.stops
        .map((c, i) => `${c} ${Math.round((i / (n - 1)) * 100)}%`)
        .join(', ')})`;
    const light = p.text === 'light';
    const ink = light ? '#ffffff' : '#2a2320';
    return {
        gradient,
        ink,
        badgeBg: light ? 'rgba(255,255,255,0.20)' : 'rgba(0,0,0,0.10)',
        badgeBorder: light ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.15)',
    };
};

// Long date for the "next post drops" banner, e.g. "June 30, 2026".
const fmtLongDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// --- Pure helpers (no component state) ---------------------------------------

// ISO timestamp -> { day, month, year } for the card's date badge.
const fmtDate = (iso) => {
    const d = iso ? new Date(iso) : new Date();
    if (Number.isNaN(d.getTime())) return { day: '--', month: '', year: '' };
    return {
        day: String(d.getDate()).padStart(2, '0'),
        month: d.toLocaleString('en-US', { month: 'short' }),
        year: String(d.getFullYear()),
    };
};

// Short drop label for the lock pill, e.g. "Jun 30".
const fmtDropBadge = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
};

// API post (PostResponse) -> the shape the carousel cards render.
const mapPost = (p) => ({
    id: p.id,
    title: p.title,
    preview: p.excerpt || (p.content ? `${p.content.slice(0, 160).trim()}…` : ''),
    readTime: `${p.read_time || 1} min read`,
    category: (p.tags && p.tags[0] && p.tags[0].name) || 'Article',
    date: fmtDate(p.published_at || p.created_at),
    slug: p.slug,
    // Scheduled-drop state from the backend.
    isLocked: Boolean(p.is_locked),
    dropDate: p.drop_date || null,
});

// Shown until the first real post loads (or if there are none yet / API is down).
const PLACEHOLDER_FEATURED = {
    id: 'featured-placeholder',
    title: 'First article coming soon',
    date: fmtDate(null),
    readTime: '— min read',
    category: 'SpinoSoftBits',
    preview:
        'Field notes on shipping real products — Python, React, and the messy decisions in between. Check back shortly.',
    slug: null,
    isLocked: false,
    dropDate: null,
};

const Blog = () => {
    // Data (fetched live from the blog API).
    const [featuredPost, setFeaturedPost] = useState(PLACEHOLDER_FEATURED);
    const [blogPosts, setBlogPosts] = useState([]); // rotating carousel cards
    const [nextDrop, setNextDrop] = useState(null);  // soonest scheduled post's drop date
    const [loadError, setLoadError] = useState(false);

    // Carousel state.
    const [currentIndex, setCurrentIndex] = useState(0);  // leftmost visible card
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [isPaused, setIsPaused] = useState(false);      // hover / touch pause
    const [isRewinding, setIsRewinding] = useState(false); // slow glide back to start
    const [visibleCount, setVisibleCount] = useState(3);
    const [step, setStep] = useState(0);                  // px to move per card
    const [isLoading, setIsLoading] = useState(true);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);

    const intervalRef = useRef(null);
    const gridRef = useRef(null);

    // How far we can scroll before the last card sits flush at the right edge.
    // This is what kills the white space: we never advance past real content.
    const maxIndex = Math.max(0, blogPosts.length - visibleCount);

    // Full post URL — note the /post/ segment your blog router expects.
    const postUrl = (post) =>
        post && post.slug
            ? `${BLOG_URL.replace(/\/$/, '')}/post/${post.slug}`
            : BLOG_URL;

    const openPost = (post) => {
        if (post && post.slug) {
            window.open(postUrl(post), '_blank', 'noopener,noreferrer');
        }
    };

    // --- Fetch live posts once on mount --------------------------------------
    useEffect(() => {
        const controller = new AbortController();

        (async () => {
            try {
                const res = await fetch(
                    `${BLOG_API_URL.replace(/\/$/, '')}/posts/?limit=20`,
                    { signal: controller.signal, headers: { Accept: 'application/json' } }
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                const data = await res.json();
                // API returns published posts newest-first.
                const list = Array.isArray(data) ? data : [];

                if (list.length) {
                    // Newest post is the pinned hero; everything else rotates.
                    // (Want to pin a specific post instead? Swap the next line for:
                    //  const featuredRaw = list.find((p) => p.is_featured) || list[0];
                    //  and filter it out of `restRaw` by id.)
                    const featuredRaw = list[0];
                    const restRaw = list.slice(1);
                    setFeaturedPost(mapPost(featuredRaw));
                    setBlogPosts(restRaw.map(mapPost));

                    // The soonest still-locked carousel post drives the "next
                    // drops" banner. We exclude the hero (it shows its own pill),
                    // so the banner means "what's queued after this." ISO strings
                    // sort chronologically, so the first is the nearest.
                    const upcoming = restRaw
                        .filter((p) => p.is_locked && p.drop_date)
                        .map((p) => p.drop_date)
                        .sort()[0] || null;
                    setNextDrop(upcoming);
                } else {
                    setBlogPosts([]); // keep placeholder featured, no carousel
                    setNextDrop(null);
                }
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('[Blog] Failed to load posts:', err);
                    setLoadError(true); // keep placeholder featured, fail quietly in UI
                }
            } finally {
                setIsLoading(false);
            }
        })();

        return () => controller.abort();
    }, []);

    // Responsive visible-card count (mirrors the CSS breakpoints).
    useEffect(() => {
        const compute = () => {
            const w = window.innerWidth;
            setVisibleCount(w <= 768 ? 1 : w <= 1024 ? 2 : 3);
        };
        compute();
        window.addEventListener('resize', compute);
        return () => window.removeEventListener('resize', compute);
    }, []);

    // Keep the index in range if the viewport (and maxIndex) changes.
    useEffect(() => {
        setCurrentIndex((i) => Math.min(i, maxIndex));
    }, [maxIndex]);

    // Measure one card + gap so the transform is pixel-perfect at any breakpoint.
    useLayoutEffect(() => {
        const measure = () => {
            const grid = gridRef.current;
            if (!grid || !grid.children.length) return;
            const cardWidth = grid.children[0].getBoundingClientRect().width;
            const cs = window.getComputedStyle(grid);
            const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
            setStep(cardWidth + gap);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [visibleCount, blogPosts.length, isLoading]);

    const goNext = useCallback(() => {
        setCurrentIndex((i) => {
            if (i >= maxIndex) {
                setIsRewinding(true); // slow glide back to the first card
                return 0;
            }
            return i + 1;
        });
    }, [maxIndex]);

    const goPrev = useCallback(() => {
        setIsRewinding(false);
        setCurrentIndex((i) => (i <= 0 ? maxIndex : i - 1));
    }, [maxIndex]);

    // Auto-play: drifts slowly, pauses on hover/touch, glides back at the end.
    useEffect(() => {
        const playing = isAutoPlaying && !isPaused && !isLoading && maxIndex > 0;
        if (!playing) {
            clearInterval(intervalRef.current);
            return;
        }
        intervalRef.current = setInterval(goNext, 3500);
        return () => clearInterval(intervalRef.current);
    }, [isAutoPlaying, isPaused, isLoading, maxIndex, goNext]);

    // Clear the slow-rewind flag once its longer transition has run.
    useEffect(() => {
        if (!isRewinding) return;
        const t = setTimeout(() => setIsRewinding(false), 1300);
        return () => clearTimeout(t);
    }, [isRewinding, currentIndex]);

    const goToSlide = (index) => {
        setIsRewinding(false);
        setCurrentIndex(Math.min(index, maxIndex));
    };

    const toggleAutoPlay = () => setIsAutoPlaying((p) => !p);

    const handleTouchStart = (e) => {
        setIsPaused(true);
        const x = e.targetTouches[0].clientX;
        setTouchStart(x);
        setTouchEnd(x);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        const distance = touchStart - touchEnd;
        if (distance > 50) goNext();
        else if (distance < -50) goPrev();
        setIsPaused(false);
    };

    const transform = `translateX(-${currentIndex * step}px)`;
    const activeCardIndex = currentIndex + Math.floor(visibleCount / 2);

    const featuredClickable = Boolean(featuredPost.slug);
    const featuredPalette = paletteFor(featuredPost);

    return (
        <section className="blog section" id="blog">
            <h2 className="section__title">Latest Insights</h2>
            <span className="section__subtitle"> My thoughts on technology, Programming, and Poetry.</span>

            <div className="blog__container container">
                {/* Pinned featured / current post (does not rotate) */}
                <article
                    className="blog__featured"
                    onClick={() => openPost(featuredPost)}
                    role={featuredClickable ? 'link' : undefined}
                    tabIndex={featuredClickable ? '0' : undefined}
                    aria-label={`Featured post: ${featuredPost.title}`}
                    style={featuredClickable ? undefined : { cursor: 'default' }}
                >
                    <div
                        className="blog__featured-header"
                        style={{ background: featuredPalette.gradient }}
                    >
                        <span
                            className="blog__category"
                            style={{
                                color: featuredPalette.ink,
                                background: featuredPalette.badgeBg,
                                borderColor: featuredPalette.badgeBorder,
                            }}
                        >
                            {featuredPost.category}
                        </span>
                        <div className="blog__date-display" style={{ color: featuredPalette.ink }}>
                            <div className="blog__date-day">{featuredPost.date.day}</div>
                            <div className="blog__date-month">{featuredPost.date.month}</div>
                        </div>
                    </div>

                    <div className="blog__featured-content">
                        <span className="blog__featured-badge">★ Latest Post</span>
                        <h3 className="blog__featured-title">{featuredPost.title}</h3>
                        <p className={`blog__featured-preview ${featuredPost.isLocked ? 'blog__preview--locked' : ''}`}>
                            {featuredPost.preview}
                        </p>

                        <div className="blog__featured-footer">
                            <span className="blog__read-time">
                                <i className="uil uil-clock"></i>
                                {featuredPost.readTime}
                            </span>
                            {featuredPost.isLocked ? (
                                <span className="blog__lock-note">
                                    <i className="uil uil-lock"></i>
                                    Drops {fmtDropBadge(featuredPost.dropDate)}
                                </span>
                            ) : (
                                featuredClickable && (
                                    <button
                                        className="blog__link"
                                        onClick={(e) => { e.stopPropagation(); openPost(featuredPost); }}
                                        aria-label={`Read full post: ${featuredPost.title}`}
                                    >
                                        Read full post
                                    </button>
                                )
                            )}
                        </div>

                        {nextDrop && (
                            <div className="blog__next-post">
                                <i className="uil uil-calendar-alt"></i>
                                Next post drops <strong>{fmtLongDate(nextDrop)}</strong>
                            </div>
                        )}
                    </div>
                </article>

                {blogPosts.length > 0 && (
                    <>
                        <p className="blog__upcoming-label">More articles</p>

                        <div
                            className="blog__scroll-wrapper"
                            onMouseEnter={() => setIsPaused(true)}
                            onMouseLeave={() => setIsPaused(false)}
                        >
                            {maxIndex > 0 && (
                                <>
                                    <button
                                        className="blog__nav blog__nav--prev"
                                        onClick={goPrev}
                                        aria-label="Previous blog post"
                                    >
                                        <i className="uil uil-angle-left"></i>
                                    </button>

                                    <button
                                        className="blog__nav blog__nav--next"
                                        onClick={goNext}
                                        aria-label="Next blog post"
                                    >
                                        <i className="uil uil-angle-right"></i>
                                    </button>
                                </>
                            )}

                            <div className="blog__viewport">
                                <div
                                    className={`blog__grid ${isRewinding ? 'rewinding' : 'transitioning'}`}
                                    ref={gridRef}
                                    style={{ transform }}
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                >
                                    {blogPosts.map((post, index) => {
                                        const cp = paletteFor(post);
                                        return (
                                        <article
                                            key={post.id}
                                            className={`blog__item ${isLoading ? 'loading' : ''} ${
                                                index === activeCardIndex ? 'active' : ''
                                            }`}
                                            onClick={() => openPost(post)}
                                            role="link"
                                            tabIndex="0"
                                            aria-label={`Blog post: ${post.title}`}
                                        >
                                            <div
                                                className="blog__header"
                                                style={{ background: cp.gradient }}
                                            >
                                                <span
                                                    className="blog__category"
                                                    style={{
                                                        color: cp.ink,
                                                        background: cp.badgeBg,
                                                        borderColor: cp.badgeBorder,
                                                    }}
                                                >
                                                    {post.category}
                                                </span>
                                                <div className="blog__date-display" style={{ color: cp.ink }}>
                                                    <div className="blog__date-day">{post.date.day}</div>
                                                    <div className="blog__date-month">{post.date.month}</div>
                                                </div>
                                            </div>

                                            <div className="blog__content">
                                                <h3 className="blog__title">{post.title}</h3>
                                                <p className={`blog__preview ${post.isLocked ? 'blog__preview--locked' : ''}`}>
                                                    {post.preview}
                                                </p>

                                                <div className="blog__meta">
                                                    <div className="blog__info">
                                                        <span className="blog__read-time">
                                                            <i className="uil uil-clock"></i>
                                                            {post.readTime}
                                                        </span>
                                                    </div>
                                                    {post.isLocked ? (
                                                        <span className="blog__lock-note">
                                                            <i className="uil uil-lock"></i>
                                                            Drops {fmtDropBadge(post.dropDate)}
                                                        </span>
                                                    ) : (
                                                        <button
                                                            className="blog__link"
                                                            aria-label={`Read more about ${post.title}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openPost(post);
                                                            }}
                                                        >
                                                            Read more
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </article>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {maxIndex > 0 && (
                            <div className="blog__controls">
                                <button
                                    className="blog__play-pause"
                                    onClick={toggleAutoPlay}
                                    aria-label={isAutoPlaying ? 'Pause auto-play' : 'Start auto-play'}
                                >
                                    <i className={`uil ${isAutoPlaying ? 'uil-pause' : 'uil-play'}`}></i>
                                </button>

                                <div className="blog__indicators">
                                    {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                                        <button
                                            key={index}
                                            className={`blog__indicator ${index === currentIndex ? 'active' : ''}`}
                                            onClick={() => goToSlide(index)}
                                            aria-label={`Go to position ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className="blog__more">
                    <a
                        href={BLOG_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="blog__more-link"
                        aria-label="View all blog articles"
                    >
                        View all articles
                        <i className="uil uil-arrow-right"></i>
                    </a>
                </div>
            </div>
        </section>
    );
};

export default Blog;